import CreateButton from "../components/CreateButton"
import { useEffect, useRef, useState } from "react"
import { Helmet, HelmetProvider } from "react-helmet-async"
import api from "../api"
import { ReactComponent as FilterIcon } from "../assets/images/icons/ic-filter.svg"
import Button from "../components/Button"
import Card from "../components/cards/Card"
import TestCard from "../components/cards/TestCard"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import Pagination from "../components/Pagination"
import Select from "../components/Select"
import StatusBadge from "../components/StatusBadge"
import Table from "../components/Table"
import TextInput from "../components/TextInput"
import { TestStatus, TestType } from "../enums"
import Test from "./Test"
import styles from "./Tests.module.css"

const emptyFilters = { course_id: null, edition_id: null, module_id: null, lesson_id: null }
const questionCount = test => (test.content?.innerTests || []).reduce((total, question) => {
    if ([TestType.SingleChoice, TestType.MultipleChoice].includes(question.testType)) return total + 1
    if (question.testType === TestType.TrueFalse) return total + (question.answers?.length || 0)
    if (question.testType === TestType.TextCompletion) return total + (question.words || []).filter(word => word.hidden).length
    return total
}, 0)
const statusOf = test => {
    if (test.deleted_at) return { label: "Eliminato", color: "var(--sf-red)" }
    if (test.status === TestStatus.Draft) return { label: test.publishable ? "Bozza pronta" : "Bozza da completare", color: "var(--sf-gray)" }
    if (test.status === TestStatus.Completed) return { label: "Terminato", color: "#64748b" }
    if (new Date(test.published_at) > new Date()) return { label: "Programmato", color: "var(--sf-orange)" }
    if (test.expires_at && new Date(test.expires_at) <= new Date()) return { label: "Scaduto", color: "var(--sf-red)" }
    return { label: "In corso", color: "var(--primary)" }
}

export default function Tests() {
    const [tests, setTests] = useState([])
    const [page, setPage] = useState(0)
    const per = 25
    const [count, setCount] = useState(0)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [search, setSearch] = useState("")
    const [filters, setFilters] = useState(emptyFilters)
    const [filtersOpen, setFiltersOpen] = useState(false)
    const [courses, setCourses] = useState([])
    const [editions, setEditions] = useState([])
    const [optionSearch, setOptionSearch] = useState({})
    const [editor, setEditor] = useState(null)
    const [revision, setRevision] = useState(0)
    const request = useRef(0)
    const duplicatePending = useRef(false)
    const [duplicatingId, setDuplicatingId] = useState(null)
    const [duplicateMessage, setDuplicateMessage] = useState("")
    const duplicateTest = async test => {
        if (duplicatePending.current) return
        duplicatePending.current = true
        setDuplicatingId(test.id)
        setDuplicateMessage("")
        try {
            const copy = await api.post(`/admin/tests/${test.id}/duplicate`)
            setDuplicateMessage(`Test “${copy.name}” duplicato in bozza.`)
            setPage(0)
            refresh()
        } catch {
            setDuplicateMessage("Impossibile duplicare il test. Riprova dal menu delle azioni.")
        } finally {
            duplicatePending.current = false
            setDuplicatingId(null)
        }
    }
    const refresh = () => setRevision(value => value + 1)

    useEffect(() => {
        let ignore = false
        api.get('/admin/courses').then(data => { if (!ignore) setCourses(data) }).catch(() => { if (!ignore) setError("Impossibile caricare i corsi. Riprova.") })
        return () => { ignore = true }
    }, [revision])
    useEffect(() => {
        let ignore = false
        setEditions([])
        if (filters.course_id) api.get(`/admin/courses/${filters.course_id}/editions`).then(data => { if (!ignore) setEditions(data) }).catch(() => { if (!ignore) setError("Impossibile caricare le edizioni. Riprova.") })
        return () => { ignore = true }
    }, [filters.course_id, revision])
    useEffect(() => {
        const controller = new AbortController()
        const id = ++request.current
        const params = new URLSearchParams({ page, per })
        if (search.trim()) params.set('q', search.trim())
        Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, value) })
        setLoading(true)
        setError("")
        api.get(`/admin/tests?${params}`, { signal: controller.signal }).then(data => {
            if (id !== request.current || controller.signal.aborted) return
            const lastPage = Math.max(0, Math.ceil(data.count / per) - 1)
            if (page > lastPage) { setPage(lastPage); return }
            setTests(data.tests)
            setCount(data.count)
        }).catch(() => {
            if (!controller.signal.aborted && id === request.current) setError("Impossibile caricare i test. Riprova.")
        }).finally(() => { if (!controller.signal.aborted && id === request.current) setLoading(false) })
        return () => controller.abort()
    }, [page, search, filters, revision])

    const modules = editions.find(item => item.id === filters.edition_id)?.modules || []
    const lessons = modules.find(item => item.id === filters.module_id)?.lessons || []
    const filterFields = [
        { key: 'course_id', label: 'Corso', all: 'Tutti i corsi', options: courses },
        { key: 'edition_id', label: 'Edizione', all: 'Tutte le edizioni', options: editions, disabled: !filters.course_id },
        { key: 'module_id', label: 'Modulo', all: 'Tutti i moduli', options: modules, disabled: !filters.edition_id },
        { key: 'lesson_id', label: 'Lezione', all: 'Tutte le lezioni', options: lessons, disabled: !filters.module_id },
    ]
    const columns = [
        { key: 'name', label: 'Test', type: 'custom', wrap: true },
        { key: 'course_name', label: 'Corso', type: 'string', wrap: true },
        { key: 'edition_name', label: 'Edizione', type: 'string', wrap: true },
        { key: 'module_name', label: 'Modulo', type: 'string', wrap: true },
        { key: 'lesson_name', label: 'Lezione', type: 'string', wrap: true },
        { key: 'questions', label: 'Domande', type: 'number' },
        { key: 'threshold', label: 'Soglia', type: 'string' },
        { key: 'status_badge', label: 'Stato', type: 'custom' },
        { key: 'published_at', label: 'Pubblicazione', type: 'datetime' },
        { key: 'expires_at', label: 'Scadenza', type: 'datetime' },
        { key: 'actions', label: '', type: 'custom', handleClick: true, style: { width: 52 } },
    ]
    return <HeaderFooterLayout>
        <HelmetProvider><Helmet><title>Didattica - Test</title></Helmet></HelmetProvider>
        <div className={styles.container}><div className={styles.section}><div className={styles.sectionInner}>
            {editor ? <Test test={editor.test} onBack={() => setEditor(null)} onSave={() => { setEditor(null); refresh() }} /> : <>

                <div className={styles.header}><div><p className={styles.eyebrow}>Didattica</p><h1>Test</h1><p>Gestisci i test e consulta le attività di corsi, moduli e lezioni.</p></div><CreateButton onClick={() => setEditor({})}> Nuovo test</CreateButton></div>
                {duplicateMessage && <div className={styles.empty} role="status">{duplicateMessage}</div>}
                <Card appearance="modern" integratedTable title="Elenco test" actions={<span className={styles.count}>{loading ? 'Caricamento…' : `${count} test`}</span>}>
                    <div className={styles.search}><TextInput appearance="modern" type="search" placeholder="Cerca test" value={search} onKeyUp={value => { setSearch(value || ''); setPage(0) }} /><button type="button" className={styles.filterButton} aria-expanded={filtersOpen} aria-controls="test-filters" onClick={() => setFiltersOpen(value => !value)}><FilterIcon /> Filtri</button></div>
                    <div className={styles.filters} id="test-filters" hidden={!filtersOpen}>{filterFields.map((field, index) => {
                        const options = [{ id: -1, name: field.all }, ...[...field.options].sort((a, b) => a.name.localeCompare(b.name))]
                        return <div className={styles.filterField} key={field.key}><span>{field.label}</span><Select appearance="modern" portal disabled={field.disabled} placeholder={field.label} selected={options.find(item => item.id === (filters[field.key] || -1))} options={options.filter(item => item.id === -1 || item.name.toLocaleLowerCase().includes((optionSearch[field.key] || '').toLocaleLowerCase()))} onSearch={value => setOptionSearch(previous => ({ ...previous, [field.key]: value || '' }))} searchPlaceholder={`Cerca ${field.label.toLowerCase()}`} onSelect={item => {
                            const next = { ...filters, [field.key]: item?.id === -1 ? null : item?.id || null }
                            filterFields.slice(index + 1).forEach(child => { next[child.key] = null })
                            setFilters(next); setOptionSearch({}); setPage(0)
                        }} /></div>
                    })}</div>
                    {error ? <div className={styles.empty} role="alert">{error} <Button appearance="subtle" onClick={refresh}>Riprova</Button></div> : <Table integrated appearance="minimal" loading={loading} loadingN={10} columns={columns} elements={tests.map(test => ({
                        ...test,
                        name: { value: test.name, type: 'string', component: <div className={styles.testName}><strong>{test.name}</strong><span>{`${test.teacher?.name || ''} ${test.teacher?.surname || ''}`.trim()}</span></div> },
                        course_name: test.course?.name || '—', edition_name: test.edition?.name || '—', module_name: test.module?.name || '—', lesson_name: test.lesson?.name || '—', questions: questionCount(test), threshold: `${test.success_threshold || 0}/${questionCount(test)}`,
                        status_badge: { value: statusOf(test).label, type: 'string', component: <StatusBadge appearance="soft" {...statusOf(test)} /> },
                        actions: { value: test.id, type: 'number', component: <TestCard menuOnly test={test} onDuplicate={duplicateTest} duplicating={duplicatingId === test.id} onEdit={() => setEditor({ test })} onDelete={refresh} onComplete={refresh} /> },
                    }))} />}
                    {!error && !loading && tests.length === 0 && <div className={styles.empty}>Nessun test corrisponde ai filtri selezionati.</div>}
                    <div className={styles.pagination}><Pagination total={count} per={per} page={page} onChange={setPage} loading={loading} /></div>
                </Card>
            </>}
        </div></div></div>
    </HeaderFooterLayout>
}
